import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import { getChildSchedule } from "../../api/parentDashboardApi";
import { useSchoolFeatures } from "../../common/utils/schoolSettings";
import { effectiveSlotTimes } from "../../common/utils/classTiming";

const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };

const formatTime = (t) => {
  if (!t) return "—";
  const [hh, mm] = t.split(":");
  const h = parseInt(hh);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${h12}:${mm} ${ampm}`;
};

export default function ChildScheduleTab({ child }) {
  const features = useSchoolFeatures();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const gradeId = child?.currentGrade?.id;

  useEffect(() => {
    if (!gradeId) { setLoading(false); return; }
    setLoading(true);
    getChildSchedule(gradeId)
      .then((data) => {
        const items = Array.isArray(data) ? data : (data?.schedules || data?.items || []);
        setSchedules(items);
      })
      .catch(() => setSchedules([]))
      .finally(() => setLoading(false));
  }, [gradeId]);

  if (loading) {
    return <div className="flex justify-content-center p-5"><ProgressSpinner /></div>;
  }

  if (!gradeId) {
    return <p className="text-color-secondary text-center p-4">No grade enrolled — schedule unavailable.</p>;
  }

  if (schedules.length === 0) {
    return <p className="text-color-secondary text-center p-4">No schedule found for {child?.currentGrade?.name}.</p>;
  }

  // Group by day, sorted by day order then start_time
  const byDay = DAY_ORDER.reduce((acc, day) => {
    const daySchedules = schedules
      .filter((s) => s.day_of_week === day || (s.selected_days && s.selected_days.includes(day)))
      .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
    if (daySchedules.length) acc[day] = daySchedules;
    return acc;
  }, {});

  return (
    <div>
      <div className="flex align-items-center gap-2 mb-3">
        <i className="pi pi-calendar text-primary" />
        <span className="font-semibold">
          Class Schedule — {child?.currentGrade?.name}
        </span>
      </div>

      {Object.entries(byDay).map(([day, rows]) => (
        <div key={day} className="mb-4">
          <div className="flex align-items-center gap-2 mb-2">
            <Tag value={DAY_LABELS[day]} severity="info" />
          </div>
          <DataTable value={rows} size="small" stripedRows className="border-1 surface-border border-round">
            <Column
              header="Time"
              body={(r) => {
                const t = effectiveSlotTimes(r, features);
                return `${formatTime(t.start_time)} – ${formatTime(t.end_time)}`;
              }}
              style={{ width: "10rem", whiteSpace: "nowrap" }}
            />
            <Column
              header="Subject"
              body={(r) => r.subject?.name || "—"}
            />
            <Column
              header="Teacher"
              body={(r) => {
                const t = r.teacher;
                if (!t) return "—";
                return t.displayName || t.name || t.user?.name || "—";
              }}
            />
            <Column
              header="Classroom"
              body={(r) => r.classroom?.name || "—"}
            />
          </DataTable>
        </div>
      ))}
    </div>
  );
}
